import { ProposalSettings } from "../../../domain/proposals/settings/proposalSettings"
import { useUpdateGeneralSettingsMutation } from "../../../domain/proposals/settings/proposalSettingsQueries"
import LabeledSwitch from "../../common/LabeledSwitch"
import VSpace from "../../common/VSpace"
import SettingsCard from "../SettingsCard"
import { useState } from "react"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons"

type Props = {
  proposalSettings: ProposalSettings
}

const GeneralSettings = ({ proposalSettings }: Props) => {
  const [settings, setSettings] = useState<ProposalSettings>(proposalSettings)
  const updateGeneralSettingsMutation = useUpdateGeneralSettingsMutation()
  const redirectFeatureEnabled = useFeatureFlag('proposal-redirect-option')

  const isChanged = settings.sendProposalPdfByDefault !== proposalSettings.sendProposalPdfByDefault ||
    settings.enableSalesNotificationsOnCustomerViewsByDefault !== proposalSettings.enableSalesNotificationsOnCustomerViewsByDefault ||
    settings.estimateProposalSalesTaxByDefault !== proposalSettings.estimateProposalSalesTaxByDefault ||
    settings.redirectToDownPaymentCheckoutOnApprovalByDefault !== proposalSettings.redirectToDownPaymentCheckoutOnApprovalByDefault

  return <SettingsCard
    title="General Settings"
    description="Manage default behaviors for proposal downloads, sales notifications, and tax estimates. These settings apply to all new proposals."
    onUpdate={() => {
      updateGeneralSettingsMutation.mutate({
        sendProposalPdfByDefault: settings.sendProposalPdfByDefault,
        enableSalesNotificationsOnCustomerViewsByDefault: settings.enableSalesNotificationsOnCustomerViewsByDefault,
        estimateProposalSalesTaxByDefault: settings.estimateProposalSalesTaxByDefault,
        redirectToDownPaymentCheckoutOnApprovalByDefault: settings.redirectToDownPaymentCheckoutOnApprovalByDefault
      })
    }}
    isUpdating={updateGeneralSettingsMutation.isLoading}
    updateDisabled={!isChanged}
  >
    <VSpace>
      <LabeledSwitch
        label="Allow the customer to download a PDF copy of the proposal"
        value={settings.sendProposalPdfByDefault}
        onChange={value => setSettings({ ...settings, sendProposalPdfByDefault: value })}
      />
      <LabeledSwitch
        label="Notify the sales person by default when a customer views a proposal"
        value={settings.enableSalesNotificationsOnCustomerViewsByDefault}
        onChange={value => setSettings({ ...settings, enableSalesNotificationsOnCustomerViewsByDefault: value })}
      />
      <LabeledSwitch
        label="Estimate sales tax by default when creating proposals"
        value={settings.estimateProposalSalesTaxByDefault}
        onChange={value => setSettings({ ...settings, estimateProposalSalesTaxByDefault: value })}
      />
      {redirectFeatureEnabled && <LabeledSwitch
        label={<>Redirect Customer to Payment after Proposal Approval
          <Tooltip title="Only available for proposals created when your invoicing system is set to SynkedUP Payments.">
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </>}
        value={settings.redirectToDownPaymentCheckoutOnApprovalByDefault}
        onChange={value => setSettings({ ...settings, redirectToDownPaymentCheckoutOnApprovalByDefault: value })}
      />}
    </VSpace>
  </SettingsCard>
}

export default GeneralSettings
